import React, { useMemo } from 'react';
import {
  Button, Card, Col, Form, Row,
} from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { v4 as uuidv4 } from 'uuid';
import AddSelection from './AddSelection';

import Required from '../../Required';
import useAPI from "../../../hooks/useAPI";
import useToast from "../../../hooks/useToast";
import {makeUID} from "../../../helpers";

export default function ResultCard({
  data, periods, resultTypes, onChange, letters, required, disabled
}) {
  const api = useAPI();
  const addToast = useToast();
  const addCard = () => {
    const lastId = data
      .filter((d) => d.id <= 0)
      .map((d) => d.id)
      .sort((a, b) => a - b)[0];
    const newId = lastId === undefined ? 0 : lastId - 1;
    onChange(data.concat([{
      id: makeUID(16), title: '', year: undefined, projResultTypeId: undefined, unit: '', value: '',
    }]), 'results');
  };

  const deleteCard = async (itemId) => {
    if (itemId > 0) {
      if ((await api.get(`/projs/0/results/${itemId}/checkUsage`))?.usedInBuys) {
        addToast.error('Данный результат используется в закупках проекта');
      } else {
        onChange(data.filter((d) => d.id !== itemId), 'results');
      }
    }
  };

  const onResultItemChange = (itemId, value, field) => {
    const dataCopy = [...data];
    const changedItem = data.find((d) => d.id === itemId);
    changedItem[field] = value;
    onChange(dataCopy, 'results');
  };

  const years = useMemo(
    () => periods.filter((p) => !p.isClosed).map((p) => p.year),
    [periods],
  );

  const getYearsOptions = (item, isItemEnabled) => (isItemEnabled ? (
    <>
      <option value="" />
      {years.map((year) => <option key={year} value={year}>{year}</option>)}
    </>
  ) : (
    <option value={item.year}>{item.year}</option>
  ));

  return (
    <>
      {data.map((item, index) => {
        const isItemEnabled = !item.year || years.includes(item.year);

        return (
          <Row key={index}>
            <Form.Group as={Col}>
              <Card className="mb-4">
                <Card.Header style={cardHeaderStyling}>
                  {isItemEnabled && data.length > 1 && <Button onClick={() => deleteCard(item.id)} variant="light" className="btn-close" size="sm" />}
                </Card.Header>
                <Card.Body>
                  <Row className="mb-4">
                    <Form.Group as={Col}>
                      <Form.Label>
                        Уникальный результат отчетного периода
                        {required && <Required />}
                      </Form.Label>
                      <Form.Control
                        placeholder="Укажите, что будет создано (основной результат (актив), который будет предоставлен заказчику). Для количественно измеримых результатов обязательным является указание планируемого значения"
                        required={required}
                        as="textarea"
                        rows={3}
                        value={item.title}
                        readOnly={!isItemEnabled || disabled}
                        onChange={(e) => onResultItemChange(item.id, e.target.value, 'title')}
                      />
                      <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row className="mb-4">
                    <Form.Group as={Col} md="1">
                      <Form.Label>
                        Год
                        {required && <Required />}
                      </Form.Label>
                      <Form.Control
                        required={required}
                        as="select"
                        value={item.year}
                        disabled={!isItemEnabled || disabled}
                        onChange={(e) => onResultItemChange(item.id, parseInt(e.target.value), 'year')}
                      >
                        {getYearsOptions(item, isItemEnabled)}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">Выберите год</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="11">
                      <Form.Label>
                        Тип
                        {required && <Required />}
                      </Form.Label>
                      <Form.Control
                        required={required}
                        as="select"
                        value={item.projResultTypeId}
                        disabled={!isItemEnabled || disabled}
                        onChange={(e) => onResultItemChange(item.id, parseInt(e.target.value), 'projResultTypeId')}
                      >
                        <option value="" />
                        {resultTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">Выберите тип</Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row className="mb-4">
                    <Form.Group as={Col} md="6">
                      <Form.Label>
                        Единицы измерения
                        {required && <Required />}
                      </Form.Label>
                      <Form.Control
                        required={required}
                        type="text"
                        value={item.unit}
                        readOnly={!isItemEnabled || disabled}
                        onChange={(e) => onResultItemChange(item.id, e.target.value, 'unit')}
                      />
                      <Form.Control.Feedback type="invalid">Выберите единицу измерения</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="6">
                      <Form.Label>
                        Значение
                        {required && <Required />}
                      </Form.Label>
                      <Form.Control
                        required={required}
                        type="text"
                        value={item.value}
                        readOnly={!isItemEnabled || disabled}
                        onChange={(e) => onResultItemChange(item.id, e.target.value, 'value')}
                      />
                      <Form.Control.Feedback type="invalid">Укажите значение</Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col}>
                      <Form.Label>
                        Мероприятие Постановления
                        {required && <Required />}
                      </Form.Label>
                      <AddSelection
                        title="Выбор мероприятий Постановления"
                        options={letters.map((l) => ({ key: l.code, text: `${l.code}) ${l.descr}` }))}
                        selectedValues={[item.letterCode]}
                        readOnly={!isItemEnabled || disabled}
                        onChange={(value) => onResultItemChange(item.id, value, 'letterCode')}
                        singleChoice
                        required={required}
                        addButtonLabel="Выбрать"
                      />
                      <Form.Control.Feedback type="invalid">Выберите мероприятие</Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
            </Form.Group>
          </Row>
        );
      })}
      <Row className="mb-4">
        <Form.Group as={Col} md="12">
          <Button onClick={addCard} disabled={disabled} variant="outline-primary">
            <PlusLg className="me-1 mb-1" />
            Добавить результат
          </Button>
        </Form.Group>
      </Row>
    </>
  );
}

const cardHeaderStyling = {
  display: 'flex', justifyContent: 'end', minHeight: 38,
};
